import { html, css } from "lit";
import { customElement, property, query, queryAll } from "lit/decorators.js";
import { msg } from "@omegagrid/localize";
import { ContextIndicator } from "@reporting/rp-types/analytics";
import { DropdownList, Input, OmegaComponent, Switch, events } from "@omegagrid/core";
import { rendererTypes } from "../visualizations/renderers";

@customElement('rp-fsection-indicators-settings')
export class IndicatorSettingsSection extends OmegaComponent {

	static styles = [OmegaComponent.styles, css`
		:host {
			padding-top: 4px;
		}

		.row {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;
			width: 180px;
			margin-bottom: 4px;
		}

		.col-1, .col-2 {
			padding: 0 5px;
		}

		.col-1 {
			flex: 0 0 65px;
		}

		.col-2 {
			flex: 1;
			overflow: hidden;
		}

		og-switch {
			height: 18px;
			width: 30px;
		}

		og-dropdown-list, og-input {
			width: 100%;
		}
	`];

	@property({type: Object})
	indicator: ContextIndicator;

	@queryAll('.field')
	fields: NodeListOf<HTMLElement>;

	updateIndicator() {
		this.fields.forEach(field => {
			const key = field.dataset.name as keyof ContextIndicator;
			let value: any;
			if (field instanceof Switch) {
				value = field.state == 'on';
			} else if (field instanceof Input) {
				value = field.value;
			} else if (field instanceof DropdownList) {
				value = field.value;
			} else {
				value = null;
			}

			if (key == 'display_unit') value = parseInt(value);

			(this.indicator[key] as any) = value;
		});

		this.dispatchEvent(new events.ChangeEvent({value: this.indicator}));
	}

	updated() {
		setTimeout(() => this.requestUpdate(), 1); // TODO: odd bug causing fields to not update properly
	}

	render = () => this.indicator ? html`
		<div class="row">
			<div class="col-1">${msg('Alias')}</div>
			<div class="col-2">
				<og-input
					class="field"
					data-name="name"
					.value="${this.indicator.name || ''}"
					@change="${() => this.updateIndicator()}">
				</og-input>
			</div>
		</div>

		<div class="row">
			<div class="col-1">${msg('Display unit')}</div>
			<div class="col-2">
				<og-dropdown-list
					@change="${() => this.updateIndicator()}"
					class="field"
					data-name="display_unit"
					.value="${this.indicator.display_unit || '1'}"
					.items="${[
						{key: '1', value: msg('None')},
						{key: '1000', value: msg('Thousands')},
						{key: '1000000', value: msg('Millions')},
						{key: '1000000000', value: msg('Billions')}
					]}">
				</og-dropdown-list>
			</div>
		</div>

		<div class="row">
			<div class="col-1">${msg('Values')}</div>
			<div class="col-2">
				<og-dropdown-list
					@change="${() => this.updateIndicator()}"
					class="field"
					data-name="percentage"
					.value="${this.indicator.percentage || '0'}"
					.items="${[
						{key: '0', value: msg('Absolute')},
						{key: '1', value: `${msg('Relative')} (${msg('Subtotal')})`},
						{key: '2', value: `${msg('Relative')} (${msg('Total')})`}
					]}">
				</og-dropdown-list>
			</div>
		</div>

		<div class="row">
			<div class="col-1">${msg('Renderer')}</div>
			<div class="col-2">
				<og-dropdown-list
					@change="${() => this.updateIndicator()}"
					class="field"
					data-name="renderer"
					.value="${this.indicator.renderer || 'value'}"
					.items="${rendererTypes.map(rt => ({key: rt.k, value: rt.v}))}">
				</og-dropdown-list>
			</div>
		</div>

		<div class="row">
			<div class="col-1">${msg('Hidden')}</div>
			<div class="col-2">
				<og-switch
					class="field"
					data-name="hidden"
					shape="hexagon"
					.state="${this.indicator.hidden ? 'on' : 'off'}"
					@change="${() => this.updateIndicator()}">
				</og-switch>
			</div>
		</div>
	` : '';

}
